import termsAndConditionsPdfFile from '@/assets/pdfs/Curby Terms and Conditions.pdf';
import { ContentPanel } from '@/components/content-panel';
import { EmailInput } from '@/components/forms/email-input/component';
import { Form } from '@/components/forms/form';
import { getYupSchemaForPassword } from '@/components/forms/password-input/checks/utils';
import { PasswordSection } from '@/components/forms/password-input/component';
import { Checkbox, Flex, TextInput } from '@mantine/core';
import { FormikProps } from 'formik';
import * as yup from 'yup';
import styles from './styles.module.css';

// Define a yup validation schema for the signup form
// it should enforce that the email and reenterEmail are the same
// the password requires a minimum of 8 characters, one uppoercase and one number
// the reenterPassword value must match the password value
const yupSchemaForPassword = getYupSchemaForPassword('password', 'reenterPassword');
const validationSchema = yup
	.object({
		firstName: yup
			.string()
			.required('Please enter your first name.')
			.max(40, 'First name is too long'), // Salesforce limitation
		lastName: yup
			.string()
			.required('Please enter your last name.')
			.max(40, 'Last name is too long'), // 40 chars Salesforce limitation
		email: yup
			.string()
			.required('Please enter your email.')
			.email('The email is badly formatted.')
			.max(80, 'Email is too long'), // Salesforce limitation
		reenterEmail: yup
			.string()
			.required('Please re-enter your email.')
			.email('The email is badly formatted.')
			.oneOf([yup.ref('email')], 'The emails must match.'),
		agreeToTerms: yup.boolean().isTrue('You must agree to the terms and conditions.'),
	})
	.concat(yupSchemaForPassword);

export type SignupFormValues = {
	firstName: string;
	lastName: string;
	email: string;
	reenterEmail: string;
	password: string;
	reenterPassword: string;
	agreeToTerms: boolean;
};

const initialValues = {
	firstName: '',
	lastName: '',
	email: '',
	reenterEmail: '',
	password: '',
	reenterPassword: '',
	agreeToTerms: false,
};

type SignupFormProps = {
	onSubmit: (values: SignupFormValues) => void;
	formRef: React.RefObject<FormikProps<SignupFormValues>>;
};

const preventPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
	e.preventDefault();
};

// The input fields for email should disable copy/paste

export const SignupForm = ({ onSubmit, formRef }: SignupFormProps) => {
	return (
		<ContentPanel>
			<Form
				initialValues={initialValues}
				onSubmit={onSubmit}
				innerRef={formRef}
				validationSchema={validationSchema}
			>
				{({ getFieldProps, errors, touched }) => (
					<>
						<Flex gap="md">
							<TextInput
								label="First Name"
								placeholder="First name"
								{...getFieldProps('firstName')}
								className={styles.cell}
								error={touched.firstName && errors.firstName}
							/>
							<TextInput
								label="Last Name"
								placeholder="Last name"
								{...getFieldProps('lastName')}
								className={styles.cell}
								error={touched.lastName && errors.lastName}
							/>
						</Flex>

						<EmailInput
							label="Email Address"
							placeholder="Email address"
							{...getFieldProps('email')}
							error={touched.email && errors.email}
						/>
						<EmailInput
							label="Re-enter Email Address"
							placeholder="Re-enter email address"
							onPaste={preventPaste}
							{...getFieldProps('reenterEmail')}
							error={touched.reenterEmail && errors.reenterEmail}
						/>
						<PasswordSection
							passwordProps={{
								label: 'Password',
								placeholder: '******',
								...getFieldProps('password'),
								error: touched.password && errors.password,
							}}
							reenterPasswordProps={{
								label: 'Re-enter Password',
								placeholder: '******',
								...getFieldProps('reenterPassword'),
								error: touched.reenterPassword && errors.reenterPassword,
							}}
						/>
						<Checkbox
							variant="filled"
							alt="terms of use and privacy policy"
							label={
								<span className={styles.termsLinks}>
									I agree to the Curby{' '}
									<a href={termsAndConditionsPdfFile} download>
										Terms of use
									</a>{' '}
									and{' '}
									<a
										href="https://acrobat.adobe.com/id/urn:aaid:sc:AP:443e2dfb-bda4-42b5-9be6-dd4a20dcb353?viewer%21megaVerb=group-discover"
										target="_blank"
										rel="noreferrer"
									>
										privacy policy.
									</a>
								</span>
							}
							{...getFieldProps('agreeToTerms')}
							error={touched.agreeToTerms && errors.agreeToTerms}
						/>
					</>
				)}
			</Form>
		</ContentPanel>
	);
};
